import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  TextField,
  MenuItem,
  Stack,
  Paper,
  Divider,
  Chip,
  alpha,
  useTheme,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Download as DownloadIcon, Refresh as RefreshIcon, Storage as StorageIcon, History as HistoryIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { ethereumService } from '../utils/ethereum';
import { listRecoverySnapshots } from '../utils/recoveryBackup';
import { useProject } from '../projects/ProjectContext';

const MotionPaper = motion(Paper);

const pillSx = {
  borderRadius: 999,
  px: 2,
  fontWeight: 750,
  textTransform: 'none',
};

const gradientPrimarySx = {
  ...pillSx,
  backgroundImage:
    'linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(99, 102, 241, 1) 45%, rgba(34, 211, 238, 1) 100%)',
  color: '#08090e',
  boxShadow: '0 8px 28px rgba(168, 85, 247, 0.26)',
  '&:hover': {
    backgroundImage:
      'linear-gradient(90deg, rgba(187, 110, 255, 1) 0%, rgba(115, 122, 255, 1) 45%, rgba(56, 225, 255, 1) 100%)',
    boxShadow: '0 10px 32px rgba(168, 85, 247, 0.32)',
  },
  '&.Mui-disabled': {
    backgroundImage: 'none',
    color: alpha('#e9e7ff', 0.42),
    boxShadow: 'none',
  },
};

const RECOVERY_LAST_BACKUP_AT_KEY = 'recovery.lastBackupAt';

function downloadTextFile({ filename, mime, text }) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const Recovery = () => {
  const theme = useTheme();
  const accentPurple = theme.palette.primary.main;
  const accentCyan = theme.palette.secondary.main;

  const [loading, setLoading] = useState(false);
  const { currentProjectId } = useProject();
  const [wallets, setWallets] = useState(() => JSON.parse(localStorage.getItem('wallets') || '[]'));
  const [selectedSnapshotId, setSelectedSnapshotId] = useState('');

  const [lastBackupAt, setLastBackupAt] = useState(() => localStorage.getItem(RECOVERY_LAST_BACKUP_AT_KEY) || '');
  const snapshots = useMemo(() => listRecoverySnapshots({ projectId: currentProjectId }), [currentProjectId, loading]);
  const selectedSnapshot = useMemo(
    () => snapshots.find((s) => s.id === selectedSnapshotId) || null,
    [snapshots, selectedSnapshotId],
  );

  const walletCount = Array.isArray(wallets) ? wallets.length : 0;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const fromApi = await ethereumService.loadWallets();
      const fromLocal = JSON.parse(localStorage.getItem(currentProjectId ? `wallets.${currentProjectId}` : 'wallets') || '[]');
      const localByAddr = new Map(fromLocal.map((w) => [w.address, w]));
      const merged = (fromApi || []).map((w) => ({ ...w, ...(localByAddr.get(w.address) || {}) }));
      setWallets(merged);
      localStorage.setItem(currentProjectId ? `wallets.${currentProjectId}` : 'wallets', JSON.stringify(merged));
      toast.success('Wallet list refreshed');
    } catch (e) {
      const fromLocal = JSON.parse(localStorage.getItem(currentProjectId ? `wallets.${currentProjectId}` : 'wallets') || '[]');
      setWallets(fromLocal);
      toast.error(e?.message || 'Failed to refresh from server');
    } finally {
      setLoading(false);
    }
  }, [currentProjectId]);

  useEffect(() => {
    const fromLocal = JSON.parse(localStorage.getItem(currentProjectId ? `wallets.${currentProjectId}` : 'wallets') || '[]');
    setWallets(fromLocal);
  }, [currentProjectId]);

  useEffect(() => {
    if (snapshots.length === 0) {
      setSelectedSnapshotId('');
      return;
    }
    if (!selectedSnapshotId || !snapshots.some((s) => s.id === selectedSnapshotId)) {
      setSelectedSnapshotId(snapshots[0].id);
    }
  }, [snapshots, selectedSnapshotId]);

  const exportJson = useCallback(() => {
    const backup = {
      version: 1,
      exportedAt: new Date().toISOString(),
      wallets: (wallets || []).map((w) => ({
        address: w.address,
        privateKey: w.privateKey,
        buyAmount: w.buyAmount ?? '',
        fundAmount: w.fundAmount ?? '',
      })),
    };

    const missingKeys = backup.wallets.filter((w) => !w.privateKey).length;
    if (missingKeys > 0) {
      toast.warning(`${missingKeys} wallet(s) missing privateKey in storage`);
    }

    const filename = `wallet-backup-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    const ts = new Date().toISOString();
    downloadTextFile({ filename, mime: 'application/json', text: JSON.stringify(backup, null, 2) });
    localStorage.setItem(RECOVERY_LAST_BACKUP_AT_KEY, ts);
    setLastBackupAt(ts);
    toast.success('Backup downloaded');
  }, [wallets]);

  const exportSnapshot = useCallback(() => {
    if (!selectedSnapshot) return toast.error('Select a snapshot');
    const filename = `recovery-snapshot-${selectedSnapshot.type}-${selectedSnapshot.at.replace(/[:.]/g, '-')}.json`;
    downloadTextFile({ filename, mime: 'application/json', text: JSON.stringify(selectedSnapshot, null, 2) });
    toast.success('Snapshot downloaded');
  }, [selectedSnapshot]);

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto' }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 900,
          letterSpacing: 0.6,
          backgroundImage: `linear-gradient(90deg, ${accentPurple} 0%, #6366f1 45%, ${accentCyan} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        Backup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 2.5, maxWidth: 600 }}>
        Export JSON with private keys for this environment, or download auto-captured snapshots after wallet saves and key exports.
      </Typography>

      <Alert
        severity="warning"
        sx={{
          mb: 2.5,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.warning.main, 0.1),
          border: `1px solid ${alpha(theme.palette.warning.main, 0.32)}`,
          color: alpha(theme.palette.warning.light, 0.98),
          '& .MuiAlert-icon': { color: theme.palette.warning.light },
        }}
      >
        This will export <strong>private keys</strong>. Only download on a trusted device and store offline (encrypted vault or cold storage).
      </Alert>

      <MotionPaper
        elevation={0}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${alpha(accentPurple, 0.22)}`,
          background: `linear-gradient(165deg, ${alpha(accentPurple, 0.06)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 46%, ${alpha(accentCyan, 0.04)} 100%)`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <Stack divider={<Divider flexItem sx={{ borderColor: alpha('#fff', 0.06) }} />}>
          <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    bgcolor: alpha(accentPurple, 0.14),
                    border: `1px solid ${alpha(accentPurple, 0.3)}`,
                    color: '#ede9fe',
                  }}
                >
                  <StorageIcon sx={{ opacity: 0.95 }} fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 880 }}>
                    Wallet backup
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 1.25 }}>
                    Merge of server wallets + local buy/fund fields for the active project cache.
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
                    <Chip
                      size="small"
                      label={`${walletCount} wallet${walletCount === 1 ? '' : 's'}`}
                      sx={{
                        fontWeight: 760,
                        borderRadius: 2,
                        border: `1px solid ${alpha(accentCyan, 0.35)}`,
                        bgcolor: alpha(accentCyan, 0.1),
                      }}
                    />
                    {lastBackupAt ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Last: ${new Date(lastBackupAt).toLocaleString()}`}
                        sx={{ fontWeight: 650, borderRadius: 2, borderColor: alpha('#fff', 0.14) }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="No backup timestamp yet"
                        sx={{ borderRadius: 2, borderColor: alpha('#fff', 0.12) }}
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                <Tooltip title="Reload from API + merge local cache">
                  <span>
                    <IconButton
                      onClick={() => void refresh()}
                      disabled={loading}
                      size="small"
                      sx={{
                        border: `1px solid ${alpha('#fff', 0.14)}`,
                        borderRadius: 2,
                        bgcolor: alpha('#fff', 0.03),
                      }}
                    >
                      {loading ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={exportJson}
                  disabled={loading}
                  startIcon={<DownloadIcon />}
                  sx={{ ...gradientPrimarySx, py: 0.95 }}
                >
                  Download backup
                </Button>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    bgcolor: alpha(accentCyan, 0.12),
                    border: `1px solid ${alpha(accentCyan, 0.32)}`,
                    color: '#cffafe',
                  }}
                >
                  <HistoryIcon sx={{ opacity: 0.95 }} fontSize="small" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 880 }}>
                    Auto backups
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    Snapshots from saves, master/creation exports, and auto-export after generate/import. Pick one to download as JSON.
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'stretch' }} sx={{ width: '100%' }}>
                <TextField
                  select
                  label="Snapshot"
                  value={selectedSnapshotId}
                  onChange={(e) => setSelectedSnapshotId(e.target.value)}
                  size="small"
                  sx={{
                    flex: { sm: '1 1 0%' },
                    minWidth: 0,
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  }}
                >
                  {snapshots.length === 0 ? (
                    <MenuItem value="" disabled>
                      No snapshots yet
                    </MenuItem>
                  ) : (
                    snapshots.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {new Date(s.at).toLocaleString()} • {s.type}
                        {s.label ? ` • ${s.label}` : ''}
                      </MenuItem>
                    ))
                  )}
                </TextField>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={exportSnapshot}
                  startIcon={<DownloadIcon />}
                  sx={{
                    ...gradientPrimarySx,
                    py: 1,
                    flexShrink: 0,
                    alignSelf: { xs: 'stretch', sm: 'center' },
                    whiteSpace: { sm: 'nowrap' },
                  }}
                >
                  Download snapshot
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </MotionPaper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: { xs: 'left', sm: 'center' } }}>
        Prefer an air‑gapped machine for full-wallet exports. Rotate keys if a file may have leaked.
      </Typography>
    </Box>
  );
};

export default Recovery;
